import React, { useEffect, useState, memo, useRef } from "react";
import "./PGDetails.css";
import { useParams, useNavigate } from "react-router-dom";
import {
  doc,
  getDoc,
  setDoc,
  deleteDoc,
  collection,
  getDocs,
  onSnapshot,
  runTransaction,
  serverTimestamp,
} from "firebase/firestore";
import { getDownloadURL, ref } from "firebase/storage";
import { db, storage, auth } from "./firebase";
import { useAuth } from "../context/AuthContext";
import ContactOwnerForm from "./ContactOwnerForm";
import {
  FaRoad,
  FaHeart,
  FaWifi,
  FaTv,
  FaHome,
  FaCheckCircle,
  FaCamera,
  FaBath,
  FaBroom,
  FaBed,
  FaChair,
  FaFan,
  FaCar,
} from "react-icons/fa";
import { BiFridge } from "react-icons/bi";
import { MdFastfood, MdKitchen, MdOutlineWaterDrop } from "react-icons/md";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/css";
import "swiper/css/navigation";
import { Navigation } from "swiper/modules";
import "swiper/css/pagination";

const amenityIcons = {
  wifi: <FaWifi />,
  geyser: <FaCheckCircle />,
  tv: <FaTv />,
  "ro water": <MdOutlineWaterDrop />,
  "washing machine": <FaCheckCircle />,
  refrigerator: <BiFridge />,
  cctv: <FaCamera />,
  "food service": <MdFastfood />,
  "proper bathroom": <FaBath />,
  "cooking facility": <MdKitchen />,
  "room cleaning": <FaBroom />,
  bed: <FaBed />,
  wardrobe: <FaChair />,
  "table chair": <FaChair />,
  "ac rooms": <FaFan />,
  parking: <FaCar />,
};

const UNLOCK_DURATION = 30 * 60 * 1000; // 30 minutes

const optimizeImage = (url, maxWidth = 1200, maxHeight = 1200, quality = 0.8) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = "Anonymous";
    img.src = url;
    img.onload = () => {
      const scale = Math.min(maxWidth / img.width, maxHeight / img.height, 1);
      const width = img.width * scale;
      const height = img.height * scale;
      const canvas = document.createElement("canvas");
      canvas.width = width;
      canvas.height = height;
      const ctx = canvas.getContext("2d");
      ctx.drawImage(img, 0, 0, width, height);

      const logo = new Image();
      logo.crossOrigin = "Anonymous";
      logo.src = process.env.PUBLIC_URL + "/apnaghar-192.png";

      logo.onload = () => {
        const logoWidth = width / 4;
        const logoHeight = (logo.height / logo.width) * logoWidth;
        const x = (width - logoWidth) / 2;
        const y = (height - logoHeight) / 2;
        ctx.globalAlpha = 0.3;
        ctx.drawImage(logo, x, y, logoWidth, logoHeight);
        ctx.globalAlpha = 1.0;
        resolve(canvas.toDataURL("image/jpeg", quality));
      };
      logo.onerror = () => resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => resolve(url);
  });
};

const distanceKm = (lat1, lon1, lat2, lon2) => {
  const toRad = (v) => (v * Math.PI) / 180;
  const R = 6371;
  const dLat = toRad(lat2 - lat1);
  const dLon = toRad(lon2 - lon1);
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
};

const PGDetails = () => {
  const [listingData, setListingData] = useState(null);
  const [sliderItems, setSliderItems] = useState([]);
  const [activeIndex, setActiveIndex] = useState(0);
  const [cityState, setCityState] = useState("");
  const [exactLocation, setExactLocation] = useState("");
  const [isSaved, setIsSaved] = useState(false);
  const [tokens, setTokens] = useState(0);
  const [isButtonUnlocked, setIsButtonUnlocked] = useState(false);
  const [remainingTime, setRemainingTime] = useState(0);
  const [avgRating, setAvgRating] = useState(0);
  const [ratingCount, setRatingCount] = useState(0);
  const [userRating, setUserRating] = useState(0);
  const [showContactForm, setShowContactForm] = useState(false);
  const [nearbyEssentials, setNearbyEssentials] = useState([]);

  const { currentUser } = useAuth();
  const { listingId } = useParams();
  const navigate = useNavigate();

  const capitalizeWords = (str) => str.replace(/\b\w/g, (c) => c.toUpperCase());
  const formatTime = (ms) => {
    const totalSeconds = Math.floor(ms / 1000);
    const minutes = Math.floor(totalSeconds / 60);
    const seconds = totalSeconds % 60;
    return `${minutes}m ${seconds}s`;
  };
useEffect(() => {
  // Disable right-click
  const handleContextMenu = (e) => e.preventDefault();

  // Disable specific keys: Ctrl+S, Ctrl+U, F12
  const handleKeyDown = (e) => {
    if ((e.ctrlKey && ["s", "u", "S", "U"].includes(e.key)) || e.key === "F12") {
      e.preventDefault();
    }
  };

  // Disable dragging
  const handleDragStart = (e) => e.preventDefault();

  // Disable long-press on mobile
  let touchTimer;
  const handleTouchStart = (e) => {
    touchTimer = setTimeout(() => e.preventDefault(), 400);
  };
  const handleTouchEnd = () => clearTimeout(touchTimer);

  // Add listeners
  document.addEventListener("contextmenu", handleContextMenu);
  document.addEventListener("keydown", handleKeyDown);
  document.addEventListener("dragstart", handleDragStart);
  document.addEventListener("touchstart", handleTouchStart, { passive: false });
  document.addEventListener("touchend", handleTouchEnd);

  // Cleanup
  return () => {
    document.removeEventListener("contextmenu", handleContextMenu);
    document.removeEventListener("keydown", handleKeyDown);
    document.removeEventListener("dragstart", handleDragStart);
    document.removeEventListener("touchstart", handleTouchStart);
    document.removeEventListener("touchend", handleTouchEnd);
  };
}, []);

  // ---------------- FETCH LISTING ----------------
  useEffect(() => {
    if (!listingId) return;
    const fetchListing = async () => {
      try {
        const docSnap = await getDoc(doc(db, "house-listings", listingId));
        if (!docSnap.exists()) return console.error("Listing not found");
        const data = { id: docSnap.id, ...docSnap.data() };
        setListingData(data);

        // Images
        const thumbURL = data.thumbnailPath
          ? await optimizeImage(await getDownloadURL(ref(storage, data.thumbnailPath)))
          : "";
        const slotURLs = data.slotImagePaths
          ? await Promise.all(
              data.slotImagePaths.map(async (p) => ({
                type: "image",
                url: await optimizeImage(await getDownloadURL(ref(storage, p))),
              }))
            )
          : [];
        setSliderItems([{ type: "image", url: thumbURL }, ...slotURLs]);
        setActiveIndex(0);

        setExactLocation(data.fullAddress || "Address not available");
        setCityState(data.state || "Assam");

        // ✅ Use nearbyEssentials array from Firestore directly
        setNearbyEssentials(data.nearbyEssentials || []);

        await loadRatings(data.id);
      } catch (e) {
        console.error(e);
      }
    };
    fetchListing();
  }, [listingId]);

  // ---------------- RATINGS ----------------
  const loadRatings = async (id) => {
    try {
      const ratingsSnap = await getDocs(collection(db, "house-listings", id, "ratings"));
      let sum = 0,
        count = 0,
        myRating = 0;
      ratingsSnap.forEach((d) => {
        const val = d.data()?.value;
        if (typeof val === "number") {
          sum += val;
          count++;
        }
        if (auth.currentUser?.uid === d.id) myRating = val || 0;
      });
      const avg = count ? sum / count : 0;
      setAvgRating(avg);
      setRatingCount(count);
      setUserRating(myRating);
      await setDoc(doc(db, "house-listings", id), { ratingAvg: avg, ratingCount: count }, { merge: true });
    } catch (e) {
      console.error(e);
    }
  };

  const handleRate = async (value) => {
    if (!currentUser) return alert("Please log in to rate.");
    try {
      await setDoc(
        doc(db, "house-listings", listingId, "ratings", currentUser.uid),
        { value, userId: currentUser.uid, ratedAt: new Date().toISOString() },
        { merge: true }
      );
      setUserRating(value);
      await loadRatings(listingId);
    } catch (e) {
      console.error(e);
      alert("Could not submit rating.");
    }
  };

  // ---------------- TOKENS ----------------
  useEffect(() => {
    if (!currentUser) return;
    const unsub = onSnapshot(doc(db, "user-data", currentUser.uid), (snap) => {
      if (snap.exists()) setTokens(snap.data().tokens || 0);
    });
    return () => unsub();
  }, [currentUser]);

  // ---------------- UNLOCK TIMER ----------------
  useEffect(() => {
    const unlockKey = `pg_unlock_${listingId}`;
    const unlockTime = parseInt(localStorage.getItem(unlockKey) || "0", 10);
    const now = Date.now();
    if (unlockTime && now - unlockTime < UNLOCK_DURATION) {
      setIsButtonUnlocked(true);
      setRemainingTime(UNLOCK_DURATION - (now - unlockTime));
    } else {
      setIsButtonUnlocked(false);
      setRemainingTime(0);
      localStorage.removeItem(unlockKey);
    }
  }, [listingId]);

  // ---------------- SAVED ----------------
  useEffect(() => {
    const checkSaved = async () => {
      if (!currentUser?.uid || !listingId) return;
      const savedRef = doc(db, "users", currentUser.uid, "saved-properties", listingId);
      const snap = await getDoc(savedRef);
      setIsSaved(snap.exists());
    };
    checkSaved();
  }, [listingId, currentUser]);

  const toggleSaveProperty = async () => {
    if (!currentUser) return alert("Please log in");
    const refDoc = doc(db, "users", currentUser.uid, "saved-properties", listingId);
    try {
      if (isSaved) {
        await deleteDoc(refDoc);
        setIsSaved(false);
      } else {
        const thumbURL = listingData.thumbnailPath ? await getDownloadURL(ref(storage, listingData.thumbnailPath)) : "";
        await setDoc(refDoc, {
          ...listingData,
          thumbnailURL: thumbURL,
          exactLocation,
          city: cityState,
          ratingAvg: avgRating,
          ratingCount,
          userId: currentUser.uid,
          saved: true,
          savedAt: new Date().toISOString(),
          type: "pg",
        });
        setIsSaved(true);
      }
    } catch (e) {
      console.error(e);
      alert("Could not save property.");
    }
  };

  const handleContactOwner = async () => {
    if (!currentUser) return alert("Please log in");
    const unlockKey = `pg_unlock_${listingId}`;
    const now = Date.now();
    try {
      const unlockRef = doc(db, "user-data", currentUser.uid, "contact-unlocks", listingId);
      const unlockSnap = await getDoc(unlockRef);
      if (unlockSnap.exists() && now - unlockSnap.data().unlockedAt < UNLOCK_DURATION) {
        setIsButtonUnlocked(true);
        setRemainingTime(UNLOCK_DURATION - (now - unlockSnap.data().unlockedAt));
        return navigate(`/owner/${listingData.ownerId}/${listingId}`);
      }
      const userRef = doc(db, "user-data", currentUser.uid);
      await runTransaction(db, async (transaction) => {
        const snap = await transaction.get(userRef);
        if (!snap.exists()) throw new Error("User not found");
        const currentTokens = snap.data().tokens || 0;
        if (currentTokens < 5) throw new Error("Need at least 5 tokens");
        transaction.update(userRef, { tokens: currentTokens - 5 });
        setTokens(currentTokens - 5);
      });

      await setDoc(unlockRef, { unlockedAt: now }, { merge: true });
      localStorage.setItem(unlockKey, now.toString());
      setIsButtonUnlocked(true);
      await setDoc(
        doc(db, "ownerInbox", listingData.ownerId, "messages", `${currentUser.uid}_${Date.now()}`),
        { fromUser: currentUser.uid, listingId, type: "contact_request", createdAt: serverTimestamp(), seen: false }
      );
      navigate(`/owner/${listingData.ownerId}/${listingId}`);
    } catch (e) {
      console.error(e);
      alert(e.message);
    }
  };

  if (!listingData) return <div>Loading...</div>;

  const { pgName, description, rent, amenities = [], occupancy, roomType, negotiable, deposit, mapLink } = listingData;
  const mapSrc = mapLink
    ? `https://maps.google.com/maps?q=${encodeURIComponent(mapLink.split("q=")[1] || "")}&z=15&output=embed`
    : null;

  const renderActiveSliderItem = () => {
    const active = sliderItems[activeIndex];
    if (!active) return null;
    if (active.type === "video")
      return <video src={active.url} controls style={{ width: "100%", borderRadius: "12px" }} />;
    return (
      <div className="img-block">
        <img src={active.url} alt="Main" className="main-image" onContextMenu={(e) => e.preventDefault()} onDragStart={(e) => e.preventDefault()} />
        <button onClick={toggleSaveProperty} className="save-heart-btn">
          <FaHeart size={20} color={isSaved ? "red" : "gray"} />
        </button>
      </div>
    );
  };

  return (
    <div className="pg-detail-card">
      <button className="back-button" onClick={() => navigate(-1)}>← Back</button>

      <div className="pg-image-gallery">
        <div className="main-image-wrapper">{sliderItems.length > 0 && renderActiveSliderItem()}</div>
      </div>

      <div className="pg-info-section">
        <h2 className="pg-title">{pgName}</h2>
        <p className="pg-exact"><FaHome style={{ marginRight: "6px" }} />{exactLocation}</p>
        <h4>Description</h4>
        <div className="pg-description-section"><p>{description}</p></div>

        <div className="pg-title-row">
          <div className="pg-room-info-cards">
            <div className="info-card"><strong>Occupancy</strong><p>{occupancy}</p></div>
            <div className="info-card"><strong>Room Type</strong><p>{roomType}</p></div>
          </div>

          <div className="amenities-section">
            <h4 className="amenities-heading">Amenities</h4>
            <div className="amenity-icons">
              {Object.entries(amenityIcons).map(([key, icon]) => {
                const normalized = amenities.map((a) => a.toLowerCase().trim());
                return normalized.includes(key) ? (
                  <div key={key} className="amenity-item active">{icon} <span>{capitalizeWords(key)}</span></div>
                ) : null;
              })}
            </div>
          </div>

          <div className="pg-price-info">
            <div className="price-row"><span>💸 <strong>Rent:</strong></span> <span>₹{rent}/month</span></div>
            <div className="price-row"><span>🤝 <strong>Negotiable:</strong></span> <span>{negotiable ? "Yes" : "No"}</span></div>
            <div className="price-row"><span>🔐 <strong>Deposit:</strong></span> <span>{deposit}</span></div>
          </div>

          {mapSrc ? (
            <iframe src={mapSrc} width="100%" height="220" style={{ border: 0, borderRadius: "12px" }} loading="lazy" allowFullScreen title="Map" />
          ) : (
            <p style={{ color: "gray", fontStyle: "italic" }}>Map not available</p>
          )}

   

          <div className="owner-contact-section">
            <h4>Contact Owner</h4>
            {isButtonUnlocked && remainingTime > 0 && <p style={{ color: "green", fontWeight: "bold", marginBottom: "6px" }}>Unlock ends in: {formatTime(remainingTime)}</p>}
            <button className={`contact-owner-button ${isButtonUnlocked ? "unlocked" : "locked"}`} onClick={handleContactOwner}>
              {isButtonUnlocked ? "📞 Contact Owner" : `Unlock for 5 Tokens (You have ${tokens || 0})`}
            </button>
            <button className="notify-owner-button" disabled={remainingTime > 0} onClick={() => setShowContactForm(true)}>
              📩 Notify the Owner {remainingTime > 0 && `(${Math.ceil(remainingTime / 60000)} min left)`}
            </button>
            {showContactForm && <ContactOwnerForm listingId={listingId} onClose={() => setShowContactForm(false)} />}
          </div>
        </div>
      </div>
    </div>
  );
};

export default memo(PGDetails);
